import React, { useEffect, useState } from 'react'
import { Card, Button, message, Spin, Typography } from 'antd'
import { WechatOutlined, UserOutlined, SafetyOutlined } from '@ant-design/icons'
import { useSearchParams } from 'react-router-dom'
import { useAuth, isStoredSessionValid } from '../hooks/useAuth'
import { wechatLogin } from '../utils/wechat'
import './LoginPage.css'

const { Title, Text, Paragraph } = Typography

const LoginPage: React.FC = () => {
  const { login, loading, refreshAuth } = useAuth()
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [searchParams] = useSearchParams()
  // 检查URL中是否有授权码（企微常每次打开都带新 code；若本地会话仍有效则不再调 /wechat/login，避免重复写库）
  useEffect(() => {
    const code = wechatLogin.extractCodeFromUrl()
    if (!code) return

    if (isStoredSessionValid()) {
      console.log(
        'ℹ️ 本地 Token 仍有效，跳过本次 OAuth code 换票，避免重复登录覆盖资料'
      )
      const from = searchParams.get('from') || '/dashboard'
      const target = from.startsWith('/') ? from : `/${from}`
      window.history.replaceState({}, document.title, window.location.pathname)
      window.location.replace(target)
      return
    }

    console.log('🔍 检测到企业微信授权回调，code:', code)
    handleWeChatLogin(code)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅挂载时处理一次回调
  }, [])

  // 处理企业微信登录
  const handleWeChatLogin = async (code: string) => {
    setIsLoggingIn(true)
    try {
      console.log('📱 处理企业微信登录回调，code:', code)
      const result = await login(code)
      if (result.success) {
        console.log('✅ 登录成功:', result.user)
        message.success('登录成功！')
        
        // 获取目标页面
        const from = searchParams.get('from') || '/dashboard'
        
        // 清除URL中的参数
        window.history.replaceState({}, document.title, window.location.pathname)
        
        // 手动刷新认证状态
        refreshAuth()
        
        // 使用硬跳转确保页面完全刷新
        setTimeout(() => {
          console.log('🚀 跳转到目标页面:', from)
          window.location.href = from
        }, 500)
      } else {
        console.log('❌ 登录失败:', result.message)
        message.error(result.message || '登录失败')
      }
    } catch (error) {
      console.error('❌ 登录过程异常:', error)
      message.error('登录过程中发生错误')
    } finally {
      setIsLoggingIn(false)
    }
  }

  // 跳转到企业微信登录
  const handleLoginClick = () => {
    if (!wechatLogin.isInWeChatWork()) {
      message.warning('请在企业微信中打开此页面')
      return
    }
    
    const redirectUri = wechatLogin.getCurrentUrl()
    wechatLogin.redirectToLogin(redirectUri)
  }

  return (
    <div className="mobile-login">
      <div className="login-content">
        <Card className="login-card">
          <div className="login-header">
            <div className="logo-section">
              <WechatOutlined className="logo-icon" />
            </div>
            <div className="title-section">
              <Title level={3} className="login-title">
                别克精灵顾问端
              </Title>
              <Text className="login-subtitle">
                企业微信登录
              </Text>
            </div>
          </div>

          <div className="login-description">
            <Paragraph className="description-text">
              请使用企业微信登录，享受便捷的邀请函管理服务
            </Paragraph>
            
            <div className="features">
              <div className="feature-item">
                <UserOutlined className="feature-icon" />
                <div className="feature-text">安全登录</div>
              </div>
              <div className="feature-item">
                <SafetyOutlined className="feature-icon" />
                <div className="feature-text">企业认证</div>
              </div>
            </div>
          </div>

          <div className="login-button-section">
            {isLoggingIn ? (
              <div className="loading-section">
                <Spin size="large" />
                <div className="loading-text">
                  正在登录中，请稍候...
                </div>
              </div>
            ) : (
              <Button
                type="primary"
                size="large"
                icon={<WechatOutlined />}
                onClick={handleLoginClick}
                loading={loading}
                className="login-button"
              >
                企业微信登录
              </Button>
            )}
          </div>

          <div className="login-tips">
            <div className="tips-title">
              <strong>登录说明：</strong>
            </div>
            <div className="tips-content">
              <div>• 请确保您已加入企业微信组织</div>
              <div>• 登录后即可使用邀请函管理功能</div>
              <div>• 如有问题请联系系统管理员</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default LoginPage
